import {z} from 'zod';
import {ObjectIdSchema} from './zod';
import {
  AiCategoryFieldDefinitionConfig,
  AiFieldDefinition,
  AiFieldDefinitionConfig,
  FieldDefinition,
  isAiFieldDefinition,
  RefreshSettingsSchema,
  StatusSchema,
} from './enrichment';
import {ObjectId} from 'bson';
import {assertNever, DistributedOmit} from './util';

export const MaxPublishedAgents = 10;
export const MaxDraftAgents = 50;
export const MinSegmentationTestListSize = 30;

const AgentTypeSchema = z.union([
  z.literal('analyst'),
  z.literal('legacyResearch'),
  z.literal('legacyResearchAndAnalyst'),
  z.literal('research'),
  z.literal('researchAndAnalyst'),
  z.literal('legacyTeamSize'),
]);
export type AgentType = z.infer<typeof AgentTypeSchema>;
export const AgentTypes = AgentTypeSchema.options.map((option) => option.value);

export const AgentTypeLabels: Record<AgentType, string> = {
  analyst: 'AI Analyst',
  legacyResearch: 'Legacy Web Researcher',
  legacyResearchAndAnalyst: 'Legacy Web Researcher and Analyst',
  legacyTeamSize: 'Team Size Estimator',
  research: 'Web Researcher',
  researchAndAnalyst: 'Web Researcher and Analyst',
};

export const AgentSchema = z.object({
  id: ObjectIdSchema,
  label: z.string(),
  previewAccountIds: ObjectIdSchema.array(),
  refreshSettings: RefreshSettingsSchema.optional(),
  status: StatusSchema,
  type: AgentTypeSchema,
  timestamp: z.date(),
});

export type Agent = z.infer<typeof AgentSchema>;

export function getAgentFields({
  fieldDefinitions = [],
  agentId,
}: {
  fieldDefinitions?: FieldDefinition[];
  agentId: ObjectId;
}): AiFieldDefinition[] {
  return fieldDefinitions
    .filter(isAiFieldDefinition)
    .filter(({agentId: fieldAgentId}) => agentId.equals(fieldAgentId));
}

export function getAiFieldModel(): AiCategoryFieldDefinitionConfig['model'] {
  return 'gpt-4.1-mini' as const;
}

export const DataTypeMap = {
  boolean: 'boolean',
  category: 'string',
  legacyResearch: 'string',
  legacyTeamSize: 'number',
  rating: 'number',
  research: 'string',
} as const satisfies Record<
  AiFieldDefinition['config']['fieldType'],
  AiFieldDefinition['dataType']
>;

export function getAiFieldDefinition(
  config: AiFieldDefinitionConfig
): DistributedOmit<
  AiFieldDefinition,
  'id' | 'timestamp' | 'agentId' | 'label'
> {
  const commonFields = {
    type: 'ai' as const,
    status: 'draft' as const,
  };

  const createDefinition = <T extends AiFieldDefinitionConfig>(
    fieldConfig: T
  ) => ({
    ...commonFields,
    dataType: DataTypeMap[fieldConfig.fieldType],
    config: fieldConfig,
  });

  switch (config.fieldType) {
    case 'boolean':
      return createDefinition(config);
    case 'rating':
      return createDefinition(config);
    case 'category':
      return createDefinition(config);
    case 'legacyTeamSize':
      return createDefinition(config);
    case 'legacyResearch':
      return createDefinition(config);
    case 'research':
      return createDefinition(config);
    default:
      assertNever(config);
  }
}
